package one.healthium.profile.ui.update_name

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import loco.GENERAL_SAVE
import loco.UPDATE_NAME_INFO
import loco.UPDATE_NAME_SCREEN_TITLE
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppProgressButton
import one.healthium.ui.compose.components.AppSheetTopBar
import one.healthium.ui.compose.components.AppTextField
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Duration.Companion.milliseconds

interface UpdateNameEventDelegate {
    fun onCloseClicked()
    fun onNameUpdated()
}

@Composable
fun UpdateNameScreen(eventDelegate: UpdateNameEventDelegate) {
    val viewModel = koinViewModel<UpdateNameViewModel>()
    val state by viewModel.state.collectAsState()

    EventEffect(viewModel.events) { event ->
        when (event) {
            UpdateNameEvent.OnNameUpdated -> eventDelegate.onNameUpdated()
        }
    }

    UpdateNameScreenContent(
        state = state,
        onNameChanged = viewModel::onNameChanged,
        onSaveClicked = viewModel::onSaveClicked,
        onCloseClicked = eventDelegate::onCloseClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun UpdateNameScreenContent(
    state: UpdateNameUiState,
    onNameChanged: (String) -> Unit,
    onSaveClicked: () -> Unit,
    onCloseClicked: () -> Unit,
) {
    var textValue by remember {
        val text = state.name ?: ""
        mutableStateOf(
            TextFieldValue(
                text = text,
                selection = TextRange(text.length),
            )
        )
    }
    LaunchedEffect(state.name) {
        val newText = state.name ?: ""
        textValue = textValue.copy(
            text = newText,
            selection = if (textValue.text.isEmpty() && newText.isNotBlank()) {
                TextRange(newText.length)
            } else {
                textValue.selection
            }
        )
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppSheetTopBar(
            onCloseClicked = onCloseClicked,
        )
        Spacer(Modifier.height(14.dp))
        Text(
            modifier = Modifier.padding(horizontal = 32.dp),
            text = stringResource(loco.Res.string.UPDATE_NAME_SCREEN_TITLE),
            style = AppTheme.logControlStyle?.titleStyle ?: AppTheme.textStyles.secondary_30_500,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(48.dp))
        AppTextField(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 25.dp),
            value = textValue,
            onValueChange = { newValue ->
                textValue = newValue
                onNameChanged(newValue.text)
            },
            textStyle = AppTheme.textStyles.textField.copy(textAlign = TextAlign.Center),
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Words,
                imeAction = ImeAction.Done,
            ),
            KeyboardActions(
                onDone = { onSaveClicked() }
            ),
            showKeyboardAutomatically = true,
            showKeyboardDelay = 450.milliseconds,
        )
        Spacer(Modifier.height(36.dp))
        Text(
            text = stringResource(loco.Res.string.UPDATE_NAME_INFO),
            style = AppTheme.textStyles.secondary_14_400,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(modifier = Modifier.height(60.dp))
        AppProgressButton(
            modifier = Modifier.defaultMinSize(minWidth = 176.dp),
            text = stringResource(loco.Res.string.GENERAL_SAVE),
            loading = state.loading,
            onClick = onSaveClicked,
        )
        Spacer(Modifier.height(51.dp))
    }
}
